import { Cell, addHyperlinkToFirstColumn, TableData } from '../../utils/data'
import React from 'react'
import Icon, {
    CloseIcon,
    WarningIcon,
    CheckmarkIcon,
} from '@design-systems/react-icons'
export const SUCCESS_HEX = new Set(['#007e33', '#00a200']) // dark green + bright green
const ALERT_HEX = new Set(['#ffbb33', '#ffa500', '#ffc000']) // amber/orange variants
const ERROR_HEX = new Set(['#cc0000'])
export const TOTAL_PRIORITY_FILL = 'Total Priority Fill %'

export const OPERATIONS_TIME_DIMENSION_ORDER: Record<string, number> = {
    'prior day': 0,
    'week to day': 1,
    'prior week': 2,
    'month to day': 3,
    'prior month': 4,
    'year to day': 5,
}

export const sortTimePeriods = (periods: string[]): string[] =>
    [...periods].sort((a, b) => {
        const ka = a.trim().toLowerCase()
        const kb = b.trim().toLowerCase()
        return (
            (OPERATIONS_TIME_DIMENSION_ORDER[ka] ?? 99) -
            (OPERATIONS_TIME_DIMENSION_ORDER[kb] ?? 99)
        )
    })
export const getColorFromDetail = (detail: any): string | undefined => {
    return detail?.tile?.rows?.[0]?.[1]?.color || undefined
}

export const getTileHeaderProps = (
    color: string | undefined,
    metricHeaders: { [key: string]: string },
) => {
    switch (color?.toLowerCase()) {
        case '#ffbb33':
        case '#ffc000':
            return {
                className: 'metrics-header-attention',
                text: metricHeaders.needsAttention,
            }
        case '#007e33':
            return {
                className: 'hc8-bg-success',
                text: metricHeaders.atGoal,
            }
        case '#cc0000':
            return {
                className: 'hc8-bg-warning',
                text: metricHeaders.belowGoal,
            }
        default:
            return { className: 'hc8-bg-disabled', text: '' }
    }
}

export type StoreMetricDataPoint = {
    metricData: string | undefined
    name: string | undefined
    color: string | undefined
}

export const createStoreMetricChartData = (
    rows?: Cell[][],
): { storeOpsMetricData: StoreMetricDataPoint[] } => ({
    storeOpsMetricData:
        rows?.map((item: Cell[]) => ({
            metricData: item[item.length - 1]?.value?.replace('%', ''),
            name: item[0]?.value,
            color: item[item.length - 1]?.color || '#000000',
        })) ?? [],
})

const toKey = (s: string) => s?.replaceAll(' ', '') ?? ''

export const getFieldIndex = (headings: Cell[] | undefined, field: string) =>
    (headings ?? []).findIndex((h) => toKey(h.value) === field)

export function compareValues(
    a: unknown,
    b: unknown,
    dir: 'asc' | 'desc' | 'dsc' = 'asc',
) {
    const clean = (v: unknown) =>
        String(v ?? '')
            .replace(/[%,$,\s]/g, '')
            .trim()

    const sa = clean(a)
    const sb = clean(b)

    const aIsNum = sa !== '' && isFinite(Number(sa))
    const bIsNum = sb !== '' && isFinite(Number(sb))

    let result = 0

    if (aIsNum && bIsNum) {
        result = Number(sa) - Number(sb)
    } else if (aIsNum && !bIsNum) {
        // Put numeric values before non-numeric when sorting ascending
        result = -1
    } else if (!aIsNum && bIsNum) {
        result = 1
    } else {
        // Both non-numeric: push empties last for ascending
        if (sa === '' && sb !== '') result = 1
        else if (sb === '' && sa !== '') result = -1
        else {
            result = String(a ?? '').localeCompare(String(b ?? ''), undefined, {
                numeric: true,
                sensitivity: 'base',
            })
        }
    }

    if (dir === 'desc' || dir === 'dsc') result = -result
    return result
}

export const sortRowsBy = (
    rows: Cell[][],
    colIndex: number,
    dir: 'asc' | 'desc' | 'dsc',
) => {
    const sign = dir === 'asc' ? 1 : -1 // treat 'dsc' as desc
    const withIdx = rows.map((r, i) => ({ r, i })) // stable sort
    withIdx.sort((A, B) => {
        const av = A.r?.[colIndex]?.raw ?? A.r?.[colIndex]?.value ?? ''
        const bv = B.r?.[colIndex]?.raw ?? B.r?.[colIndex]?.value ?? ''
        const cmp = compareValues(av as string, bv as string)
        return sign * (cmp !== 0 ? cmp : A.i - B.i)
    })
    return withIdx.map((x) => x.r)
}

export const getCanvasColorFromHex = (
    hexColor: string | undefined | null,
): string => {
    const c = hexColor?.toLowerCase().trim()
    if (c && SUCCESS_HEX.has(c)) return 'var(--color-icon-success)'
    if (c && ALERT_HEX.has(c)) return 'var(--color-icon-caution)'
    if (c && ERROR_HEX.has(c)) return 'var(--color-background-warning-base)'
    return 'var(--color-text-placeholder)'
}

export const applyConditionalFormattingToRows = (rows: Cell[][]): Cell[][] => {
    return rows.map((row) =>
        row.map((cell) => {
            if (cell.color) {
                return {
                    ...cell,
                    ...applyCanvasConditionalFormatting(cell.value, cell.color),
                }
            }
            return cell
        }),
    )
}

export const PRIORITY_FILL_COLUMN = 'Priority Fill %'

const normalizeHeading = (heading: string | undefined) =>
    (heading ?? '').trim().toLowerCase()

/**
 * For Total Priority Fill %, blanks at-goal (success color) values in the Priority Fill % column.
 * The column is found by heading name so cards can add or reorder columns safely.
 */
export const applyConditionalFormattingWithValueHiding = (
    rows: Cell[][],
    metricName: string | null | undefined,
    headings: Cell[] | undefined,
): Cell[][] => {
    const hiddenColumnIndex =
        metricName === TOTAL_PRIORITY_FILL
            ? (headings ?? []).findIndex(
                  (heading) =>
                      normalizeHeading(heading.value) ===
                      normalizeHeading(PRIORITY_FILL_COLUMN),
              )
            : -1

    return rows.map((row) =>
        row.map((cell, cellIndex) => {
            if (cell.color) {
                const formattedCell = {
                    ...cell,
                    ...applyCanvasConditionalFormatting(cell.value, cell.color),
                }

                if (
                    cellIndex === hiddenColumnIndex &&
                    SUCCESS_HEX.has(cell.color)
                ) {
                    formattedCell.value = ''
                }

                return formattedCell
            }
            return cell
        }),
    )
}

export const applyCanvasConditionalFormatting = (
    value: string | number,
    color: string | undefined,
): Cell => {
    const cell: Cell = {
        value: String(value).replace(/\s+/g, ''),
    }

    const normalizedColor = color?.trim().toLowerCase()

    if (normalizedColor && ERROR_HEX.has(normalizedColor)) {
        cell.className = 'hc-clr-error'
        cell.icon = React.createElement(Icon, {
            svg: CloseIcon,
            className: 'hc-clr-error',
            size: 'sm',
            'aria-hidden': true,
        })
    } else if (normalizedColor && SUCCESS_HEX.has(normalizedColor)) {
        cell.className = 'hc-clr-success'
        cell.icon = React.createElement(Icon, {
            svg: CheckmarkIcon,
            className: 'hc-clr-success',
            size: 'sm',
            'aria-hidden': true,
        })
    } else if (normalizedColor && ALERT_HEX.has(normalizedColor)) {
        cell.className = 'hc-clr-alert'
        cell.icon = React.createElement(Icon, {
            svg: WarningIcon,
            className: 'hc-clr-alert',
            size: 'sm',
            'aria-hidden': true,
        })
    }

    return cell
}

/**
 * Adds clickable links to first-column cells that have drill capabilities.
 * Only applies to store locations. Footer cells are always non-clickable.
 */
export const addClickableLinksToLinkedCells = (
    tableData: TableData,
    isStoreLocation: boolean,
): TableData => {
    if (!isStoreLocation || !tableData.rows?.length) {
        return tableData
    }

    const processedRows = tableData.rows.map((row) =>
        row.map((cell, cIdx) => {
            const drill = cell.link?.drill
            const value = cell.value
            if (cIdx === 0 && drill && value) {
                const payload = { drill, value }
                return {
                    ...cell,
                    id: encodeURIComponent(JSON.stringify(payload)),
                    link: {
                        ...cell.link,
                        href: '#',
                    },
                }
            }
            return {
                ...cell,
                id: undefined,
            }
        }),
    )
    const processedFooter = tableData.footer?.map((cell) => ({
        ...cell,
        id: undefined,
        link: undefined,
    }))

    return {
        ...tableData,
        rows: processedRows,
        footer: processedFooter,
    }
}
/** Append/merge query string onto `url`. Falls back if URLSearchParams fails. */
export const appendQuery = (url: string, queryString: string): string => {
    if (!queryString) return url
    try {
        const [base, existing] = url.split('?')
        const merged = new URLSearchParams(existing || '')
        const add = new URLSearchParams(queryString)
        add.forEach((v, k) => merged.set(k, v))
        return `${base}?${merged.toString()}`
    } catch {
        return url.includes('?')
            ? `${url}&${queryString}`
            : `${url}?${queryString}`
    }
}

/** Build the preserved query string from current selections. */
export const computePreservedQuery = (
    metricTimePeriodsArr: string[],
    timeDimension: string,
    viewIndex: number,
): string => {
    const query = new URLSearchParams()
    if (metricTimePeriodsArr.length) {
        query.set('timePeriods', metricTimePeriodsArr.join(','))
    }
    if (timeDimension) {
        query.set('activeTimePeriod', timeDimension)
    }
    query.set('activeTimeIndex', String(viewIndex))
    return query.toString()
}

/**
 * Add links to first column and then append the preserved query to any known link fields.
 */
export const addHyperlinkToFirstColumnWithQueryBase = (
    table: TableData,
    loc: string,
    preservedQuery: string,
): TableData => {
    const withLinks = addHyperlinkToFirstColumn(table, loc)

    try {
        withLinks?.rows?.forEach((row) => {
            const cell: any = row?.[0]
            if (!cell || typeof cell !== 'object') return

            const apply = (obj: any, key: 'href' | 'link' | 'to') => {
                if (obj?.[key]) {
                    obj[key] = appendQuery(String(obj[key]), preservedQuery)
                }
            }

            apply(cell, 'href')
            apply(cell, 'link')
            apply(cell, 'to')

            if (cell.value && typeof cell.value === 'object') {
                apply(cell.value, 'href')
                apply(cell.value, 'link')
                apply(cell.value, 'to')
            }
        })
    } catch {
        // no-op: keep behavior consistent with original
    }

    return withLinks
}

export const splitPeriods = (timePeriods?: string): string[] => {
    if (!timePeriods) return []
    const periods: string[] = []
    const seenPeriods = new Set<string>()

    for (const period of timePeriods.split(',')) {
        const trimmedPeriod = period.trim()
        if (trimmedPeriod && !seenPeriods.has(trimmedPeriod)) {
            seenPeriods.add(trimmedPeriod)
            periods.push(trimmedPeriod)
        }
    }
    return periods
}

/** Build a name→periods[] index from the dimensions payload (detail rows only). */
export const buildDetailPeriodsIndex = (
    data?: Array<{
        page_type: string
        metric_name: string
        time_period: string
    }>,
): Record<string, string[]> => {
    const map: Record<string, string[]> = {}
    for (const it of data ?? []) {
        if (it.page_type !== 'detail') continue
        const name = it.metric_name
        const periods = splitPeriods(it.time_period)
        const existing = map[name] ?? (map[name] = [])
        for (const p of periods) {
            if (!existing.includes(p)) existing.push(p)
        }
    }
    return map
}

/** Generic helper for your repeated metric_name→row map reduce. */
export const buildMetricNameMap = <T extends { metric_name: string }>(
    rows?: T[],
): Record<string, T> =>
    (rows ?? []).reduce(
        (acc, item) => {
            acc[item.metric_name] = item
            return acc
        },
        {} as Record<string, T>,
    )

export type DrillLevel = {
    label: string
    footer: Cell[]
    drill_dimension: string
    drill_value: string
}

export const applyFooterAsTopRow = (
    tableData: TableData,
    storeLabel: string,
    headingCount: number,
): TableData => {
    const footer = tableData.footer ?? []
    if (!footer.length) return tableData

    const topRow: Cell[] = footer.map((cell, i) => ({
        ...cell,
        is_link: false,
        link: undefined,
        id: undefined,
        ...(i === 0 ? { value: storeLabel } : {}),
    }))

    return {
        ...tableData,
        rows: [topRow, ...tableData.rows],
        footer: undefined,
    }
}

export const applyDrillBreadcrumbs = (
    tableData: TableData,
    drillStack: DrillLevel[],
    headingCount: number,
): TableData => {
    if (!drillStack.length) return tableData

    // Build clickable breadcrumb rows from the drill stack
    const breadcrumbRows: Cell[][] = drillStack.map((level, stackIndex) => {
        const drillBackPayload = encodeURIComponent(
            JSON.stringify({ drillBack: true, level: stackIndex }),
        )
        return level.footer.length
            ? level.footer.map((cell, i) => ({
                  ...cell,
                  is_link: i === 0,
                  link: i === 0 ? { href: '#' } : undefined,
                  id: i === 0 ? drillBackPayload : undefined,
                  ...(i === 0 ? { value: level.label } : {}),
              }))
            : Array.from({ length: headingCount }, (_, i) =>
                  i === 0
                      ? {
                            value: level.label,
                            is_link: true,
                            link: { href: '#' },
                            id: drillBackPayload,
                        }
                      : { value: '' },
              )
    })

    // Non-clickable total row from the current footer (deepest level),
    // labeled with the category that was drilled into
    const currentFooter = tableData.footer ?? []
    const currentDrillValue = drillStack[drillStack.length - 1].drill_value
    const totalRow: Cell[][] = currentFooter.length
        ? [
              currentFooter.map((cell, i) => ({
                  ...cell,
                  is_link: false,
                  link: undefined,
                  id: undefined,
                  ...(i === 0 ? { value: currentDrillValue } : {}),
              })),
          ]
        : []

    return {
        ...tableData,
        rows: [...breadcrumbRows, ...totalRow, ...tableData.rows],
        footer: undefined,
    }
}

export const OPERATIONS_TABS = {
    OPERATIONS: 'operations',
    RECALL: 'recall',
} as const

export const DEFAULT_OPERATIONS_TAB = OPERATIONS_TABS.OPERATIONS

// Child routes that are not tabs of their own (chart view, drill-downs, etc.)
// fall back to the default tab so the parent tab stays visibly selected.
export const OPERATIONS_TAB_ROUTES: string[] = [OPERATIONS_TABS.RECALL]

export const getActiveOperationsTab = (pathname: string): string => {
    const segments = pathname.split('/').filter(Boolean)
    return (
        segments.find((segment) => OPERATIONS_TAB_ROUTES.includes(segment)) ??
        DEFAULT_OPERATIONS_TAB
    )
}
